import { maybe, Maybe, MutationData, rd } from '@passionware/monads';
import { Booking, EntityType, Expense, Production } from '__types__';
import { get, pick } from 'lodash';

import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import React, { Fragment } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import {
  createExpense,
  updateExpense
} from 'store/v1/expenses/expenses.actions.js';
import { EXPENSES_ACTIONS } from 'store/v1/expenses/expenses.constants.js';

import { closeModal, displayAlert } from 'store/v1/ui/ui.actions.js';

import { CellBookingItem, CellProductionItem } from 'v1/components/shared';

import useEvent from 'v1/helpers/hooks/useEvent';
import { Button } from 'v5/design-system/Button.js';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from 'v5/design-system/Form.js';
import { Input } from 'v5/design-system/Input.js';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent.js';
import { RateInputField } from 'v5/patterns/forms/fields/RateInputField';
import { FormSaveButton } from 'v5/patterns/forms/dialogs/FormSaveButton.js';
import { useRegisterDirtyState } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import { WithServices } from 'v5/platform/ts/services';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithSystemSettingsService } from 'v5/services/internal/SystemSettingsService/SystemSettingsService';
import SlotCategoryDropdownV5 from 'v1/components/shared/byModel/SlotCategory/SlotCategoryDropdown/SlotCategoryDropdownV5';

type ExpenseCreateModalProps = WithServices<
  [WithAppScopeService, WithSystemSettingsService]
> & {
  expense?: Expense;
  create?: boolean;
  customSubmit: (data: any) => void;
  production: Maybe<Production>;
  booking: Maybe<Booking>;
  mutation: MutationData<unknown, unknown>;
  open: boolean;
  onCancel: () => void;
};

const ExpenseCreateModal = ({
  expense = {} as Expense,
  create = false,
  customSubmit,
  production,
  booking,
  mutation,
  open,
  onCancel,
  services
}: ExpenseCreateModalProps) => {
  // REDUX
  const expenses = useSelector((state: any) => state.expenses);
  const { error } = expenses || {};
  const dispatch = useDispatch();

  // DEFAULTS
  const defaultCurrencyRd = services.systemSettingsService.useSetting(
    services.appScopeService.useResolvedScopeIdentity(),
    'defaults_currency'
  );
  const workspaceDefaultCurrency = rd.tryGet(defaultCurrencyRd)?.value;
  const defaultCurrency =
    production?.budget_currency ?? workspaceDefaultCurrency;

  // PROPS AND REFS
  const expenseFields = pick(expense, [
    'id',
    'commit_vendor',
    'commit_rate_amount',
    'commit_rate_currency',
    'commit_rate_quantity',
    'commit_rate_interval',
    'production_budget_expense_id',
    'booking_id',
    'slot_category_id'
  ]);
  const {
    id,
    commit_vendor,
    commit_rate_amount,
    commit_rate_currency,
    commit_rate_quantity,
    commit_rate_interval,
    production_budget_expense_id,
    booking_id,
    slot_category_id
  } = expenseFields;
  const budget_expenses = get(production, 'budget_expenses', []);

  // FORM SETUP
  const form = useForm({
    defaultValues: {
      id,
      commit_vendor,
      commit_rate_amount,
      commit_rate_currency,
      commit_rate_quantity: commit_rate_quantity || 1,
      commit_rate_interval,
      production_budget_expense_id,
      booking_id: booking_id || booking?.id,
      slot_category_id
    }
  });
  useRegisterDirtyState(form.formState.isDirty, {
    message: 'Expense create modal has unsaved changes'
  });

  // EFFECTS
  useEvent([EXPENSES_ACTIONS.CREATE_EXPENSE, EXPENSES_ACTIONS.UPDATE_EXPENSE], {
    onSuccess: () => {
      dispatch(closeModal());
      dispatch(displayAlert('success', 'Updated succesfully'));
    },
    onFailure: () => dispatch(displayAlert('error', error.message))
  });

  // METHODS
  function handleSubmit(values) {
    if (customSubmit) {
      customSubmit(values);
      return;
    }
    create
      ? dispatch(createExpense(values))
      : dispatch(updateExpense(id, values));
  }

  return (
    <Form {...form}>
      <FormDialogContent
        open={open}
        className="min-w-180"
        title={`${create ? 'Create' : 'Edit'} Expense`}
        mutation={mutation}
        onSubmit={form.handleSubmit(handleSubmit)}
        saveButtonSlot={
          <FormSaveButton mutation={mutation}>
            {create ? 'Create Expense' : 'Save changes'}
          </FormSaveButton>
        }
        footerContent={
          <>
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </>
        }
      >
        <div data-cy="expense-create-form" className="space-y-6">
          <div>
            <FormField
              control={form.control}
              name="slot_category_id"
              rules={{
                required: 'This field is required'
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Expense Category</FormLabel>
                  <FormControl>
                    <SlotCategoryDropdownV5
                      type="EXPENSE"
                      buttonVariant="secondary"
                      buttonSize="md"
                      children={null}
                      onSelect={(fieldName, value) => {
                        field.onChange(value);
                        const budgetExpense = find(
                          budget_expenses,
                          b_e => b_e.slot_category_id === value
                        );
                        if (budgetExpense) {
                          form.setValue(
                            'production_budget_expense_id',
                            budgetExpense.id
                          );
                        }
                      }}
                      slot={form.getValues()}
                      buttonClass="w-full"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div>
            <FormField
              control={form.control}
              name="commit_vendor"
              rules={{
                required: 'This field is required'
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Expense Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Name" autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-row gap-4 *:space-y-2 ">
            <div className="flex-1">
              <FormLabel>Rate</FormLabel>
              <RateInputField
                form={form}
                currencyName="commit_rate_currency"
                amountName="commit_rate_amount"
                intervalName="commit_rate_interval"
                placeholder="Rate"
                defaultCurrency={defaultCurrency}
                entityType={EntityType.EXPENSE}
              />
            </div>
            <div className="w-32">
              <FormLabel>Quantity</FormLabel>
              <FormField
                control={form.control}
                name="commit_rate_quantity"
                rules={{
                  required: 'This field is required'
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Qty"
                        type="number"
                        min={0}
                        value={field.value != null ? field.value : ''}
                        onChange={({ target }) =>
                          field.onChange(parseFloat(target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {!isEmpty(production) && (
            <div>
              <FormLabel>Attached Production</FormLabel>
              <div className="mt-2">
                {/* @ts-expect-error migration from js */}
                <CellProductionItem production={production} hideStatus />
              </div>
            </div>
          )}

          {!isEmpty(booking) && (
            <div>
              <FormLabel>Attached Booking</FormLabel>
              <div className="mt-2">
                <CellBookingItem booking={booking} />
              </div>
            </div>
          )}
        </div>
      </FormDialogContent>
    </Form>
  );
};

export default ExpenseCreateModal;
